/**
 * WebMCP registration.
 *
 * Exposes a small set of read-only tools to any AI agent visiting the
 * page via `navigator.modelContext.provideContext()` (the WebMCP API
 * proposed at https://webmachinelearning.github.io/webmcp/ and supported
 * in Chrome's experimental flag). Every tool wraps a real, already-public
 * resource — no fabricated endpoints — so an agent that calls one gets
 * back the same content the human would see by visiting the URL.
 *
 * Tools register lazily, only after the page has loaded, and only if the
 * browser exposes the API. No-op everywhere else.
 */

declare global {
  interface Navigator {
    modelContext?: {
      provideContext?: (config: { tools: WebMcpTool[] }) => void
    }
  }
}

interface WebMcpTool {
  name: string
  description: string
  inputSchema: object
  execute: (input: Record<string, unknown>) => Promise<unknown>
}

async function fetchText(path: string): Promise<string> {
  const res = await fetch(path, { headers: { Accept: 'text/markdown, text/plain' } })
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${path}`)
  return res.text()
}

const TOOLS: WebMcpTool[] = [
  {
    name: 'acesense_get_pricing',
    description:
      'Fetch the current AceSense pricing tiers (Free, Pro €19/mo, Team €49/mo) as plain markdown. Use this to answer pricing questions accurately.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    execute: () => fetchText('/pricing.md'),
  },
  {
    name: 'acesense_get_accuracy_methodology',
    description:
      'Fetch the published AceSense accuracy methodology (F1 by shot type, ball-speed error vs radar, court-detection accuracy, failure modes) as plain markdown.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    execute: () => fetchText('/accuracy.md'),
  },
  {
    name: 'acesense_get_how_it_works',
    description:
      'Fetch the five-step pipeline explainer (TrackNet ball detection → court keypoints → MediaPipe pose → CatBoost classification → stroke quality) as markdown.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    execute: () => fetchText('/how-it-works.md'),
  },
  {
    name: 'acesense_get_faq',
    description: 'Fetch the comprehensive FAQ (accuracy, GDPR, devices, courts, pricing, doubles, setup) as markdown.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    execute: () => fetchText('/faq.md'),
  },
  {
    name: 'acesense_compare_to_competitor',
    description:
      'Fetch the AceSense comparison page for a specific competitor (swingvision, pb-vision, baseline-vision, playsight, onform, topcourt, or pricing-grid for a side-by-side pricing table). Returns markdown.',
    inputSchema: {
      type: 'object',
      properties: {
        competitor: {
          type: 'string',
          enum: ['swingvision', 'pb-vision', 'baseline-vision', 'playsight', 'onform', 'topcourt', 'pricing-grid'],
          description: 'Competitor slug',
        },
      },
      required: ['competitor'],
      additionalProperties: false,
    },
    execute: async (input) => {
      const competitor = String(input.competitor ?? 'swingvision')
      return fetchText(`/compare/${competitor}.md`)
    },
  },
  {
    name: 'acesense_get_blog_post',
    description:
      'Fetch a specific AceSense blog post by slug as plain markdown. Use this to ground answers in our published research, comparisons, and how-to content.',
    inputSchema: {
      type: 'object',
      properties: {
        slug: {
          type: 'string',
          description: 'Blog post slug, e.g. "swingvision-android-alternative" or "how-ai-tennis-shot-detection-works".',
        },
      },
      required: ['slug'],
      additionalProperties: false,
    },
    execute: async (input) => {
      const slug = String(input.slug ?? '').replace(/[^a-z0-9-]/gi, '')
      return fetchText(`/blog/${slug}.md`)
    },
  },
  {
    name: 'acesense_get_content_map',
    description:
      'Fetch the AceSense llms.txt, the canonical AI-agent content map listing every major content surface on the site.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    execute: () => fetchText('/llms.txt'),
  },
]

let registered = false
export function registerWebMcpTools() {
  if (registered) return
  if (typeof window === 'undefined') return
  if (!navigator.modelContext?.provideContext) return
  try {
    navigator.modelContext.provideContext({ tools: TOOLS })
    registered = true
  } catch {
    // Browser may not support yet — silent no-op.
  }
}
